import * as React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The one switch, everywhere. Radix-style API: { checked, onCheckedChange,
 * disabled }. We don't pull in @radix-ui/react-switch just for this - saves
 * ~10kb gzipped.
 *
 * Styled to the product's reference: deep green track when on, grey when off,
 * and the knob CARRIES the state - a tick or a cross - so it reads at a glance
 * for a colourblind viewer or at a 20px render. The knob is explicitly
 * anchored (left-0.5): an absolutely-positioned knob with no `left` falls back
 * to the button's centred static position and slides off the track edge, which
 * is exactly how two earlier hand-rolled versions broke.
 */
export const Switch = React.forwardRef(
  ({ className, checked = false, onCheckedChange, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      data-state={checked ? 'checked' : 'unchecked'}
      disabled={disabled}
      onClick={() => onCheckedChange?.(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-emerald-700 dark:bg-emerald-600' : 'bg-muted-foreground/25',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'pointer-events-none absolute left-0.5 flex size-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      >
        {checked
          ? <Check className="size-3 text-emerald-700" strokeWidth={3.5} />
          : <X className="size-3 text-muted-foreground" strokeWidth={3.5} />}
      </span>
    </button>
  )
);
Switch.displayName = 'Switch';
